// client/src/components/pages/TelegramBI/ScheduleManager/CreateScheduleModal.js
import React, { useState, useEffect } from 'react';
import {
  FaTimes,
  FaClock,
  FaCheck,
  FaSearch,
  FaCalendarAlt,
  FaRocket,
  FaInfoCircle,
  FaChevronRight,
  FaChevronLeft,
  FaFileAlt,
  FaTelegramPlane,
  FaDatabase,
  FaGlobe,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../../services/api';
import ThemedSelect from '../../../common/ThemedSelect';
import ThemedTimePicker from '../../../common/ThemedTimePicker';
import RecurrencePicker from './RecurrencePicker';

const TIMEZONES = [
  'UTC',
  'Africa/Johannesburg',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
];

const CreateScheduleModal = ({ onClose, onSubmit }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    scheduleName: '',
    description: '',
    queryId: '',
    templateId: '',
    scheduleType: 'cron',
    cronExpression: '',
    intervalMinutes: 60,
    intervalUnit: 'minutes',
    scheduledTime: '',
    timeZone: 'UTC',
    targetChatId: '',
    messageFormat: 'text',
  });

  const [queries, setQueries] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [chats, setChats] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const steps = [
    { id: 1, title: 'Basic Info', icon: FaInfoCircle },
    { id: 2, title: 'Query & Template', icon: FaDatabase },
    { id: 3, title: 'Schedule', icon: FaClock },
    { id: 4, title: 'Delivery', icon: FaTelegramPlane },
  ];

  useEffect(() => {
    loadFormData();
  }, []);

  const loadFormData = async () => {
    setLoadingData(true);
    try {
      const [queriesRes, templatesRes, chatsRes] = await Promise.allSettled([
        api.telegramBI.queries.getAll({ limit: 200 }),
        api.telegramBI.templates.getAll({ limit: 200 }),
        api.telegramBI.bot.getChats({ limit: 200 }),
      ]);

      // Extract queries
      if (queriesRes.status === 'fulfilled') {
        const d = queriesRes.value.data;
        const list =
          d?.data?.queries ||
          d?.queries ||
          (Array.isArray(d?.data) ? d.data : null) ||
          (Array.isArray(d) ? d : []);
        setQueries(list);
      }

      // Extract templates
      if (templatesRes.status === 'fulfilled') {
        const d = templatesRes.value.data;
        const list =
          d?.data?.templates ||
          d?.templates ||
          (Array.isArray(d?.data) ? d.data : null) ||
          (Array.isArray(d) ? d : []);
        setTemplates(list);
      }

      // Extract chats
      if (chatsRes.status === 'fulfilled') {
        const d = chatsRes.value.data;
        const list =
          d?.data?.chats ||
          d?.chats ||
          (Array.isArray(d?.data) ? d.data : null) ||
          (Array.isArray(d) ? d : []);
        setChats(list);
      }
    } catch (error) {
      console.error('Error loading form data:', error);
      toast.error('Failed to load form data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateStep = (step) => {
    const errors = {};
    switch (step) {
      case 1:
        if (!formData.scheduleName.trim()) {
          errors.scheduleName = 'Schedule name is required';
        }
        break;
      case 2:
        if (!formData.queryId) {
          errors.queryId = 'Please select a query';
        }
        if (!formData.templateId) {
          errors.templateId = 'Please select a template';
        }
        break;
      case 3:
        if (formData.scheduleType === 'cron' && !formData.cronExpression.trim()) {
          errors.cronExpression = 'Cron expression is required';
        }
        if (formData.scheduleType === 'recurring') {
          const val = parseInt(formData.intervalMinutes, 10);
          if (!val || val < 1) {
            errors.intervalMinutes = 'Valid interval is required';
          }
        }
        if (formData.scheduleType === 'once' && !formData.scheduledTime) {
          errors.scheduledTime = 'Date and time is required';
        }
        break;
      case 4:
        if (!formData.targetChatId) {
          errors.targetChatId = 'Please select a target chat';
        }
        break;
      default:
        break;
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(4, prev + 1));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    try {
      const payload = {
        scheduleName: formData.scheduleName.trim(),
        queryId: formData.queryId,
        templateId: formData.templateId,
        targetChatId: formData.targetChatId,
        scheduleType: formData.scheduleType,
        timeZone: formData.timeZone,
      };

      if (formData.description.trim()) {
        payload.description = formData.description.trim();
      }

      switch (formData.scheduleType) {
        case 'cron':
          payload.cronExpression = formData.cronExpression.trim();
          break;
        case 'recurring': {
          let minutes = parseInt(formData.intervalMinutes, 10) || 1;
          // Convert to minutes based on selected unit
          if (formData.intervalUnit === 'hours') minutes *= 60;
          else if (formData.intervalUnit === 'days') minutes *= 1440;
          payload.intervalMinutes = minutes;
          break;
        }
        case 'once':
          payload.scheduledTime = formData.scheduledTime;
          break;
        default:
          break;
      }

      await onSubmit(payload);
    } catch (error) {
      // Error handling done in parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center space-x-2 py-4 border-b border-gray-200 px-6">
      {steps.map((step, idx) => (
        <React.Fragment key={step.id}>
          <button
            onClick={() => {
              // Only allow navigating to completed or current steps
              if (step.id <= currentStep) setCurrentStep(step.id);
            }}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              step.id === currentStep
                ? 'bg-primary-500 text-white'
                : step.id < currentStep
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-400'
            }`}
          >
            <step.icon size={11} />
            <span>{step.title}</span>
          </button>
          {idx < steps.length - 1 && (
            <div
              className={`w-6 h-px ${step.id < currentStep ? 'bg-primary-400' : 'bg-gray-200'}`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Schedule Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="scheduleName"
          value={formData.scheduleName}
          onChange={handleInputChange}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
            validationErrors.scheduleName ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="e.g., Daily Sales Report"
        />
        {validationErrors.scheduleName && (
          <p className="mt-1 text-xs text-red-600">{validationErrors.scheduleName}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Optional description of what this schedule does"
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-5">
      {/* Query Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Query <span className="text-red-500">*</span>
        </label>
        {loadingData ? (
          <div className="text-sm text-gray-400 py-2">Loading queries...</div>
        ) : (
          <ThemedSelect
            name="queryId"
            value={formData.queryId}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              validationErrors.queryId ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            <option value="">Select a query...</option>
            {queries.map((q) => (
              <option key={q.query_id} value={q.query_id}>
                {q.query_name}
              </option>
            ))}
          </ThemedSelect>
        )}
        {validationErrors.queryId && (
          <p className="mt-1 text-xs text-red-600">{validationErrors.queryId}</p>
        )}
        {!loadingData && queries.length === 0 && (
          <p className="mt-1 text-xs text-amber-600">No queries available. Create a query first.</p>
        )}
      </div>

      {/* Template Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Template <span className="text-red-500">*</span>
        </label>
        {loadingData ? (
          <div className="text-sm text-gray-400 py-2">Loading templates...</div>
        ) : (
          <ThemedSelect
            name="templateId"
            value={formData.templateId}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              validationErrors.templateId ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            <option value="">Select a template...</option>
            {templates.map((t) => (
              <option key={t.template_id} value={t.template_id}>
                {t.template_name} {t.template_type ? `(${t.template_type})` : ''}
              </option>
            ))}
          </ThemedSelect>
        )}
        {validationErrors.templateId && (
          <p className="mt-1 text-xs text-red-600">{validationErrors.templateId}</p>
        )}
        {!loadingData && templates.length === 0 && (
          <p className="mt-1 text-xs text-amber-600">
            No templates available. Create a template first.
          </p>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-5">
      {/* Schedule Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Type</label>
        <div className="flex space-x-3">
          {[
            { value: 'cron', label: 'Cron', icon: FaClock, desc: 'Flexible cron-based scheduling' },
            {
              value: 'recurring',
              label: 'Interval',
              icon: FaCalendarAlt,
              desc: 'Run at fixed intervals',
            },
            {
              value: 'once',
              label: 'One-time',
              icon: FaRocket,
              desc: 'Run once at a specific time',
            },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setFormData((prev) => ({ ...prev, scheduleType: opt.value }))}
              className={`flex-1 p-3 border rounded-lg text-left transition-all ${
                formData.scheduleType === opt.value
                  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2 mb-1">
                <opt.icon
                  size={13}
                  className={
                    formData.scheduleType === opt.value ? 'text-primary-500' : 'text-gray-400'
                  }
                />
                <span
                  className={`text-sm font-medium ${formData.scheduleType === opt.value ? 'text-primary-700' : 'text-gray-700'}`}
                >
                  {opt.label}
                </span>
              </div>
              <p className="text-xs text-gray-500">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Cron Recurrence Picker */}
      {formData.scheduleType === 'cron' && (
        <div>
          <RecurrencePicker value={formData.cronExpression} onChange={handleInputChange} />
          {validationErrors.cronExpression && (
            <p className="mt-1 text-xs text-red-600">{validationErrors.cronExpression}</p>
          )}
        </div>
      )}

      {/* Interval */}
      {formData.scheduleType === 'recurring' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Run every <span className="text-red-500">*</span>
          </label>
          <div className="flex space-x-2">
            <input
              type="number"
              name="intervalMinutes"
              value={formData.intervalMinutes}
              onChange={handleInputChange}
              min="1"
              className={`w-24 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                validationErrors.intervalMinutes ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            <ThemedSelect
              name="intervalUnit"
              value={formData.intervalUnit}
              onChange={(e) => {
                const unit = e.target.value;
                setFormData((prev) => {
                  let mins = parseInt(prev.intervalMinutes, 10) || 1;
                  // Convert to new unit base
                  if (unit === 'hours') mins = Math.max(1, Math.round(mins));
                  if (unit === 'days') mins = Math.max(1, Math.round(mins));
                  return { ...prev, intervalUnit: unit, intervalMinutes: mins };
                });
              }}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </ThemedSelect>
          </div>
          {validationErrors.intervalMinutes && (
            <p className="mt-1 text-xs text-red-600">{validationErrors.intervalMinutes}</p>
          )}
        </div>
      )}

      {/* One-time */}
      {formData.scheduleType === 'once' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Date & Time <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="date"
              value={(formData.scheduledTime || '').split('T')[0] || ''}
              onChange={(e) => {
                const timePart = (formData.scheduledTime || '').split('T')[1] || '08:00';
                const newVal = e.target.value ? `${e.target.value}T${timePart}` : '';
                handleInputChange({ target: { name: 'scheduledTime', value: newVal } });
              }}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                validationErrors.scheduledTime ? 'border-red-300' : 'border-slate-200'
              }`}
            />
            <ThemedTimePicker
              value={(formData.scheduledTime || '').split('T')[1]?.substring(0, 5) || '08:00'}
              onChange={(e) => {
                const datePart = (formData.scheduledTime || '').split('T')[0] || '';
                const newVal = datePart ? `${datePart}T${e.target.value}` : '';
                handleInputChange({ target: { name: 'scheduledTime', value: newVal } });
              }}
            />
          </div>
          {validationErrors.scheduledTime && (
            <p className="mt-1 text-xs text-red-600">{validationErrors.scheduledTime}</p>
          )}
        </div>
      )}

      {/* Timezone */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          <FaGlobe size={11} className="inline mr-1 text-gray-400" />
          Timezone
        </label>
        <ThemedSelect
          name="timeZone"
          value={formData.timeZone}
          onChange={handleInputChange}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          {TIMEZONES.map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </ThemedSelect>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-5">
      {/* Target Chat */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Target Chat <span className="text-red-500">*</span>
        </label>
        {loadingData ? (
          <div className="text-sm text-gray-400 py-2">Loading chats...</div>
        ) : (
          <ThemedSelect
            name="targetChatId"
            value={formData.targetChatId}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              validationErrors.targetChatId ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            <option value="">Select a chat...</option>
            {chats.map((chat) => (
              <option key={chat.chat_record_id} value={chat.chat_record_id}>
                {chat.chat_title ||
                  chat.title ||
                  chat.first_name ||
                  chat.username ||
                  `Chat ${chat.chat_id}`}
                {chat.chat_type ? ` (${chat.chat_type})` : ''}
              </option>
            ))}
          </ThemedSelect>
        )}
        {validationErrors.targetChatId && (
          <p className="mt-1 text-xs text-red-600">{validationErrors.targetChatId}</p>
        )}
        {!loadingData && chats.length === 0 && (
          <p className="mt-1 text-xs text-amber-600">
            No chats available. Register a Telegram chat first.
          </p>
        )}
      </div>

      {/* Message Format */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Message Format</label>
        <div className="flex space-x-3">
          {[
            { value: 'text', label: 'Plain Text' },
            { value: 'html', label: 'HTML' },
            { value: 'markdown', label: 'Markdown' },
          ].map((fmt) => (
            <button
              key={fmt.value}
              type="button"
              onClick={() => setFormData((prev) => ({ ...prev, messageFormat: fmt.value }))}
              className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                formData.messageFormat === fmt.value
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {fmt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Schedule Summary</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Name:</span>
            <span className="font-medium text-gray-800">{formData.scheduleName || '--'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Query:</span>
            <span className="font-medium text-gray-800">
              {queries.find((q) => String(q.query_id) === String(formData.queryId))?.query_name ||
                '--'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Template:</span>
            <span className="font-medium text-gray-800">
              {templates.find((t) => String(t.template_id) === String(formData.templateId))
                ?.template_name || '--'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Type:</span>
            <span className="font-medium text-gray-800 capitalize">{formData.scheduleType}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Target:</span>
            <span className="font-medium text-gray-800">
              {chats.find((c) => String(c.chat_record_id) === String(formData.targetChatId))
                ?.chat_title ||
                chats.find((c) => String(c.chat_record_id) === String(formData.targetChatId))
                  ?.first_name ||
                formData.targetChatId ||
                '--'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Timezone:</span>
            <span className="font-medium text-gray-800">{formData.timeZone}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Create Schedule</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Step {currentStep} of {steps.length}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <FaTimes size={18} />
          </button>
        </div>

        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">{renderStepContent()}</div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={currentStep === 1 ? onClose : prevStep}
            className="flex items-center space-x-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-surface border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FaChevronLeft size={10} />
            <span>{currentStep === 1 ? 'Cancel' : 'Back'}</span>
          </button>

          {currentStep < 4 ? (
            <button
              onClick={nextStep}
              className="flex items-center space-x-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors"
            >
              <span>Next</span>
              <FaChevronRight size={10} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex items-center space-x-1.5 px-5 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <FaClock size={12} className="animate-spin" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <FaCheck size={12} />
                  <span>Create Schedule</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateScheduleModal;
